import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, FileText } from "lucide-react";
import type { DocumentTemplate } from "@/hooks/useDocumentTemplates";

type Resolver = (value: DocumentTemplate | null) => void;

type PickEvent = {
  templates: DocumentTemplate[];
  resolve: Resolver;
};

const EVENT_NAME = "thiia:template-picker:open";

/** Imperatively ask the user to choose a template among several. */
export function openTemplatePicker(
  templates: DocumentTemplate[],
): Promise<DocumentTemplate | null> {
  return new Promise((resolve) => {
    window.dispatchEvent(
      new CustomEvent<PickEvent>(EVENT_NAME, {
        detail: { templates, resolve },
      }),
    );
  });
}

export default function TemplatePickerHost() {
  const [state, setState] = useState<PickEvent | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<PickEvent>).detail;
      if (!detail) return;
      setState(detail);
      const def = detail.templates.find((t) => t.is_default);
      setSelectedId(def?.id ?? detail.templates[0]?.id ?? null);
    };
    window.addEventListener(EVENT_NAME, handler as EventListener);
    return () => window.removeEventListener(EVENT_NAME, handler as EventListener);
  }, []);

  const close = (value: DocumentTemplate | null) => {
    state?.resolve(value);
    setState(null);
    setSelectedId(null);
  };

  const open = !!state;
  const templates = state?.templates ?? [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(null); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Choisir un modèle</DialogTitle>
          <DialogDescription>
            Plusieurs modèles sont disponibles. Sélectionnez celui à utiliser pour cet export.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
          {templates.map((t) => {
            const isSelected = selectedId === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setSelectedId(t.id)}
                className={`flex items-center gap-3 rounded-lg border p-3 text-left transition-colors ${
                  isSelected
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted/50"
                }`}
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-2">
                    {t.name || "Sans nom"}
                    {t.is_default && (
                      <span className="text-[13px] font-normal text-muted-foreground rounded-sm bg-muted px-1.5 py-0.5">
                        par défaut
                      </span>
                    )}
                  </div>
                </div>
                {isSelected && <Check className="h-4 w-4 text-primary" />}
              </button>
            );
          })}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => close(null)}>
            Sans modèle
          </Button>
          <Button
            onClick={() => {
              const tpl = templates.find((t) => t.id === selectedId) ?? null;
              close(tpl);
            }}
            disabled={!selectedId}
          >
            Utiliser ce modèle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
